import { Action, ActionCreator, Dispatch } from "redux";
import {
	OnboardingState,
	CurrentGuideProgress,
	OnboardingMaps
} from "../reducers";
import { ThunkAction } from "redux-thunk";
import {
	getChecklistBarData,
	getDocumentTemplateData,
	getDummyClientData,
	updateCheckListBarData
} from "../../services";
import {
	CheckListBarData,
	Modules,
	UpdateCheckListBarDataRequest
} from "../../contracts";
import { Client, Template } from "@/Contracts";

export enum OnboardingStateActionTypes {
	UPDATE_CURRENT_GUIDE_PROGRESS = "UPDATE_CURRENT_GUIDE_PROGRESS",
	UPDATE_PROGRESS_INITIALIZATION = "UPDATE_PROGRESS_INITIALIZATION",
	UPDATE_ONBOARDING_MAPS = "UPDATE_ONBOARDING_MAPS",

	CHECKLIST_BAR_DATA_REQUESTED = "CHECKLIST_BAR_DATA_REQUESTED",
	CHECKLIST_BAR_DATA_UPDATE_REQUESTED = "CHECKLIST_BAR_DATA_UPDATE_REQUESTED",
	CHECKLIST_BAR_DATA_SUCCESSFUL = "CHECKLIST_BAR_DATA_SUCCESSFUL",
	CHECKLIST_BAR_DATA_FAILED = "CHECKLIST_BAR_DATA_FAILED",

	UPDATE_MODULE_ORDER = "UPDATE_MODULE_ORDER",

	DUMMY_CLIENT_DATA_REQUESTED = "DUMMY_CLIENT_DATA_REQUESTED",
	DUMMY_CLIENT_DATA_SUCCESSFUL = "DUMMY_CLIENT_DATA_SUCCESSFUL",
	DUMMY_CLIENT_DATA_FAILED = "DUMMY_CLIENT",

	DOCUMENT_TEMPLATE_DATA_REQUESTED = "DOCUMENT_TEMPLATE_DATA_REQUESTED",
	DOCUMENT_TEMPLATE_DATA_SUCCESSFUL = "DOCUMENT_TEMPLATE_DATA_SUCCESSFUL",
	DOCUMENT_TEMPLATE_DATA_FAILED = "DOCUMENT_TEMPLATE_DATA_FAILED"
}

export interface UpdateActiveProgressDataAction
	extends Action<OnboardingStateActionTypes.UPDATE_CURRENT_GUIDE_PROGRESS> {
	readonly currentGuideProgress: CurrentGuideProgress;
}

export interface UpdateProgressInitializationAction
	extends Action<OnboardingStateActionTypes.UPDATE_PROGRESS_INITIALIZATION> {
	readonly isProgressInitialized: boolean;
}

export interface UpdateOnboardingMapsAction
	extends Action<OnboardingStateActionTypes.UPDATE_ONBOARDING_MAPS> {
	readonly onboardingMaps: OnboardingMaps;
}

export type ChecklistBarDataRequestedAction =
	Action<OnboardingStateActionTypes.CHECKLIST_BAR_DATA_REQUESTED>;

export type ChecklistBarDataUpdateRequestedAction =
	Action<OnboardingStateActionTypes.CHECKLIST_BAR_DATA_UPDATE_REQUESTED>;
export interface ChecklistBarDataSuccessfulAction
	extends Action<OnboardingStateActionTypes.CHECKLIST_BAR_DATA_SUCCESSFUL> {
	readonly checkListBarData: CheckListBarData;
}
export type ChecklistBarDataFailedAction =
	Action<OnboardingStateActionTypes.CHECKLIST_BAR_DATA_FAILED>;

export interface UpdateModuleOrderAction
	extends Action<OnboardingStateActionTypes.UPDATE_MODULE_ORDER> {
	readonly moduleOrder: Modules[];
}

export type DummyClientDataRequestedAction =
	Action<OnboardingStateActionTypes.DUMMY_CLIENT_DATA_REQUESTED>;
export interface DummyClientDataSuccessfulAction
	extends Action<OnboardingStateActionTypes.DUMMY_CLIENT_DATA_SUCCESSFUL> {
	readonly clientDetail: Client | null;
}
export type DummyClientDataFailedAction =
	Action<OnboardingStateActionTypes.DUMMY_CLIENT_DATA_FAILED>;

export type DocumentTemplateDataRequestedAction =
	Action<OnboardingStateActionTypes.DOCUMENT_TEMPLATE_DATA_REQUESTED>;
export interface DocumentTemplateDataSuccessfulAction
	extends Action<OnboardingStateActionTypes.DOCUMENT_TEMPLATE_DATA_SUCCESSFUL> {
	readonly documentTemplate: Template | null;
}
export type DocumentTemplateDataFailedAction =
	Action<OnboardingStateActionTypes.DOCUMENT_TEMPLATE_DATA_FAILED>;

export type OnboardingStateActions =
	| UpdateActiveProgressDataAction
	| UpdateProgressInitializationAction
	| UpdateOnboardingMapsAction
	| ChecklistBarDataRequestedAction
	| ChecklistBarDataSuccessfulAction
	| ChecklistBarDataFailedAction
	| UpdateModuleOrderAction
	| DummyClientDataRequestedAction
	| DummyClientDataSuccessfulAction
	| DummyClientDataFailedAction
	| DocumentTemplateDataRequestedAction
	| DocumentTemplateDataSuccessfulAction
	| DocumentTemplateDataFailedAction;

export interface OnBoardingStateDispatch {
	updateActiveProgressData: ActionCreator<
		ThunkAction<
			UpdateActiveProgressDataAction,
			OnboardingState,
			OnboardingState,
			UpdateActiveProgressDataAction
		>
	>;

	updateProgressInitilization: ActionCreator<
		ThunkAction<
			UpdateProgressInitializationAction,
			OnboardingState,
			boolean,
			UpdateProgressInitializationAction
		>
	>;

	updateOnboardingMaps: ActionCreator<
		ThunkAction<
			UpdateOnboardingMapsAction,
			OnboardingState,
			OnboardingMaps,
			UpdateOnboardingMapsAction
		>
	>;

	updateModuleOrder: ActionCreator<
		ThunkAction<
			UpdateModuleOrderAction,
			OnboardingState,
			Modules[],
			UpdateModuleOrderAction
		>
	>;

	getChecklistBarData: ActionCreator<
		ThunkAction<
			Promise<CheckListBarData>,
			OnboardingState,
			null,
			ChecklistBarDataSuccessfulAction
		>
	>;

	updateCheckListBarData: ActionCreator<
		ThunkAction<
			Promise<CheckListBarData>,
			OnboardingState,
			CheckListBarData,
			ChecklistBarDataSuccessfulAction
		>
	>;

	getDummyClientData: ActionCreator<
		ThunkAction<
			Promise<Client | null>,
			OnboardingState,
			null,
			ChecklistBarDataSuccessfulAction
		>
	>;

	getDocumentTemplateData: ActionCreator<
		ThunkAction<
			Promise<Template | null>,
			OnboardingState,
			null,
			ChecklistBarDataSuccessfulAction
		>
	>;
}

export const OnboardingActionCreators: OnBoardingStateDispatch = {
	updateActiveProgressData: (currentGuideProgress: CurrentGuideProgress) => {
		return (dispatch: Dispatch): UpdateActiveProgressDataAction => {
			const updateActiveProgressDataAction: UpdateActiveProgressDataAction =
				{
					type: OnboardingStateActionTypes.UPDATE_CURRENT_GUIDE_PROGRESS,
					currentGuideProgress
				};
			return dispatch(updateActiveProgressDataAction);
		};
	},
	updateProgressInitilization: (isProgressInitialized: boolean) => {
		return (dispatch: Dispatch): UpdateProgressInitializationAction => {
			const updateProgressInitializationAction: UpdateProgressInitializationAction =
				{
					type: OnboardingStateActionTypes.UPDATE_PROGRESS_INITIALIZATION,
					isProgressInitialized
				};
			return dispatch(updateProgressInitializationAction);
		};
	},
	updateOnboardingMaps: (onboardingMaps: OnboardingMaps) => {
		return (dispatch: Dispatch): UpdateOnboardingMapsAction => {
			const updateOnboardingMapsAction: UpdateOnboardingMapsAction = {
				type: OnboardingStateActionTypes.UPDATE_ONBOARDING_MAPS,
				onboardingMaps
			};
			return dispatch(updateOnboardingMapsAction);
		};
	},
	updateModuleOrder:
		(moduleOrder: Modules[]) =>
		(dispatch: Dispatch): UpdateModuleOrderAction => {
			const updateActiveProgressDataAction: UpdateModuleOrderAction = {
				type: OnboardingStateActionTypes.UPDATE_MODULE_ORDER,
				moduleOrder
			};
			return dispatch(updateActiveProgressDataAction);
		},

	getChecklistBarData: () => {
		return async (dispatch: Dispatch): Promise<CheckListBarData> => {
			const getChecklistBarDataRequestedAction: ChecklistBarDataRequestedAction =
				{
					type: OnboardingStateActionTypes.CHECKLIST_BAR_DATA_REQUESTED
				};
			dispatch(getChecklistBarDataRequestedAction);
			try {
				const checklistBarDataResponse = await getChecklistBarData();
				const getChecklistBarDataSuccessfulAction: ChecklistBarDataSuccessfulAction =
					{
						type: OnboardingStateActionTypes.CHECKLIST_BAR_DATA_SUCCESSFUL,
						checkListBarData: checklistBarDataResponse
					};
				dispatch(getChecklistBarDataSuccessfulAction);
				return checklistBarDataResponse;
			} catch (error) {
				const getChecklistBarDatatFailedAction: ChecklistBarDataFailedAction =
					{
						type: OnboardingStateActionTypes.CHECKLIST_BAR_DATA_FAILED
					};
				dispatch(getChecklistBarDatatFailedAction);
				return Promise.reject(error);
			}
		};
	},

	updateCheckListBarData: ({
		requestPayload,
		hasAPIUpdate = false
	}: UpdateCheckListBarDataRequest) => {
		return async (
			dispatch: Dispatch,
			getState: () => OnboardingState
		): Promise<CheckListBarData> => {
			const checklistBarDataUpdateRequestedAction: ChecklistBarDataUpdateRequestedAction =
				{
					type: OnboardingStateActionTypes.CHECKLIST_BAR_DATA_UPDATE_REQUESTED
				};
			dispatch(checklistBarDataUpdateRequestedAction);
			try {
				let checklistBarDataResponse: CheckListBarData =
					getState().checkListBarData ?? ({} as CheckListBarData);
				if (hasAPIUpdate) {
					let progressId = getState().checkListBarData?.id;
					if (!progressId) {
						const checklistBarDataResponse =
							await getChecklistBarData();
						const getChecklistBarDataSuccessfulAction: ChecklistBarDataSuccessfulAction =
							{
								type: OnboardingStateActionTypes.CHECKLIST_BAR_DATA_SUCCESSFUL,
								checkListBarData: checklistBarDataResponse
							};
						dispatch(getChecklistBarDataSuccessfulAction);
						progressId = checklistBarDataResponse.id;
					}
					checklistBarDataResponse = await updateCheckListBarData(
						progressId,
						requestPayload
					);
				} else if (checklistBarDataResponse) {
					checklistBarDataResponse = {
						...checklistBarDataResponse,
						...requestPayload
					};
				}
				if (checklistBarDataResponse) {
					const getChecklistBarDataSuccessfulAction: ChecklistBarDataSuccessfulAction =
						{
							type: OnboardingStateActionTypes.CHECKLIST_BAR_DATA_SUCCESSFUL,
							checkListBarData: checklistBarDataResponse
						};
					dispatch(getChecklistBarDataSuccessfulAction);
				}
				return checklistBarDataResponse;
			} catch (error) {
				const getChecklistBarDatatFailedAction: ChecklistBarDataFailedAction =
					{
						type: OnboardingStateActionTypes.CHECKLIST_BAR_DATA_FAILED
					};
				dispatch(getChecklistBarDatatFailedAction);
				return Promise.reject(error);
			}
		};
	},

	getDummyClientData: () => {
		return async (
			dispatch: Dispatch,
			getState: () => OnboardingState
		): Promise<Client | null> => {
			const getDummyClientDataRequestedAction: DummyClientDataRequestedAction =
				{
					type: OnboardingStateActionTypes.DUMMY_CLIENT_DATA_REQUESTED
				};
			dispatch(getDummyClientDataRequestedAction);
			try {
				const oldClientDetail = getState().clientDetail;
				if (oldClientDetail) {
					return oldClientDetail;
				}
				const {
					data: [clientDetail]
				} = await getDummyClientData();
				const getDummyClientDataSuccessfulAction: DummyClientDataSuccessfulAction =
					{
						type: OnboardingStateActionTypes.DUMMY_CLIENT_DATA_SUCCESSFUL,
						clientDetail
					};
				dispatch(getDummyClientDataSuccessfulAction);
				return clientDetail;
			} catch (error) {
				const getDummyClientDataFailedAction: DummyClientDataFailedAction =
					{
						type: OnboardingStateActionTypes.DUMMY_CLIENT_DATA_FAILED
					};
				dispatch(getDummyClientDataFailedAction);
				return Promise.reject(error);
			}
		};
	},

	getDocumentTemplateData: () => {
		return async (
			dispatch: Dispatch,
			getState: () => OnboardingState
		): Promise<Template | null> => {
			const documentTemplateDataRequestedAction: DocumentTemplateDataRequestedAction =
				{
					type: OnboardingStateActionTypes.DOCUMENT_TEMPLATE_DATA_REQUESTED
				};
			dispatch(documentTemplateDataRequestedAction);
			try {
				const oldDocumentTemplate = getState().documentTemplate;
				if (oldDocumentTemplate) {
					return oldDocumentTemplate;
				}
				const documentTemplate = await getDocumentTemplateData();
				const documentTemplateDataSuccessfulAction: DocumentTemplateDataSuccessfulAction =
					{
						type: OnboardingStateActionTypes.DOCUMENT_TEMPLATE_DATA_SUCCESSFUL,
						documentTemplate
					};
				dispatch(documentTemplateDataSuccessfulAction);
				return documentTemplate;
			} catch (error) {
				const documentTemplateDataFailedAction: DocumentTemplateDataFailedAction =
					{
						type: OnboardingStateActionTypes.DOCUMENT_TEMPLATE_DATA_FAILED
					};
				dispatch(documentTemplateDataFailedAction);
				return Promise.reject(error);
			}
		};
	}
};

export default OnboardingActionCreators;
